import { FC, useEffect } from 'react';
//i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useFormattedDate from 'app/hooks/useFormattedDate';
import useFormattedTime from 'app/hooks/useFormattedTime';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Models
import { RootState } from 'app/store';
// Async
import { getFacts } from 'app/store/EpisodeFacts/EpisodeFacts.async';
// Actions
import { EpisodesActions } from 'app/store/Episodes/Episodes.slice';
// Selectors
import { selectEpisodeEntity, selectSelectedEpisodeId } from 'app/store/Episodes/Episodes.selectors';
import { selectSortedEpisodeFacts } from 'app/store/EpisodeFacts/EpisodeFacts.selectors';
// MUI
import { Box, Tooltip, Typography } from '@mui/material';
// Icons
import {
  Close as CloseIcon,
  LabelOutlined as LabelOutlinedIcon,
  TextSnippet as TextSnippetIcon
} from '@mui/icons-material';
// Components
import { IconButton } from 'app/components/Mui';
import { InfoBlock } from 'app/components/Utilities';
import Labels from 'app/components/Labels';
import Facts from 'app/components/Facts';

const EpisodeDetails:FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const selectedEpisodeId = useAppSelector(selectSelectedEpisodeId);
  const facts = useAppSelector(selectSortedEpisodeFacts);

  const episode = useAppSelector((state:RootState) => selectEpisodeEntity(state, { episodeId: selectedEpisodeId || undefined }));
  
  const formattedDate = useFormattedDate(episode?.date);
  const formattedTime = useFormattedTime(episode?.time);

  const date = [formattedDate, formattedTime].filter(Boolean).join(' ');

  const onClose = () => {
    dispatch(EpisodesActions.setSelectedEpisodeId(null));
  }

  useEffect(() => {
    dispatch(getFacts({ episodeIds: [selectedEpisodeId] }));
    // eslint-disable-next-line
  }, [selectedEpisodeId]);

  if (!selectedEpisodeId || !episode) return null;
  return (
    <Box sx={{
      width: { xs: '50%', lg: '30%' },
      position: { xs: 'fixed', lg: 'relative' },
      top: { xs: 0, lg: 'auto' },
      right: { xs: 0, lg: 'auto' },
      p: { xs: 4, lg: 0 },
      zIndex: { xs: 1000, lg: 'auto' },
    }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          bgcolor: '#fff',
          border: '1px solid #eee',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #eee',
          }}
        >
          <Typography variant="h6">{date}</Typography>
          <IconButton name={`close-episode-details-${selectedEpisodeId}`} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: { xs: 2, lg: 3 }, overflowY: 'auto', flexGrow: 1 }}>
          {episode.labels && episode.labels.length ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Tooltip title={t('labels.labels')}>
                <LabelOutlinedIcon sx={{ color: 'rgba(0,0,0,0.54)', mr: 2 }} />
              </Tooltip>
              <Labels id="episodeFormLabels" labels={episode.labels.map((label:string) => ({ name: label, color: 'initial' }))} />
            </Box>
          ) : null}
          {facts && facts.length ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Tooltip title={t('labels.cptCodes')}>
                <TextSnippetIcon sx={{ color: 'rgba(0,0,0,0.54)', mr: 2 }} />
              </Tooltip>
              <Facts id="episodeFormFacts" facts={facts} />
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 8, mb: 4 }}>
            <InfoBlock size="large" label={t('labels.author')} value={episode.author?.name || '-'} />
            <InfoBlock size="large" label={t('labels.type')} value={episode.type || '-'} />
          </Box>
          <InfoBlock
            size="large"
            label={t('labels.notes')}
            value={episode.notes ? <Box className="ql-editor" sx={{ p: 0 }} dangerouslySetInnerHTML={{__html: episode.notes}} /> : '-'}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default EpisodeDetails;
